import React, { useState } from "react";

import {
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";

function formatStatisticValue(
  value,
  digits = 2
) {
  const number =
    Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return number.toFixed(digits);
}

export function TeamStatisticsTable({
  result,
  tableMode,
  valueMode = "average",
}) {
  const rows =
    Array.isArray(
      result?.rows
    )
      ? result.rows
      : [];

  const statisticTypes =
    result?.statistic_types &&
    typeof result.statistic_types === "object"
      ? Object.entries(
          result.statistic_types
        )
      : [];

  const [
    sortKey,
    setSortKey,
  ] = useState(
    "should_quotient"
  );

  const [
    sortDirection,
    setSortDirection,
  ] = useState(
    "desc"
  );


  function numberOrNull(
    value
  ) {
    const number =
      Number(value);

    return Number.isFinite(
      number
    )
      ? number
      : null;
  }


  function getScope(
    row
  ) {
    return (
      row?.[tableMode] ||
      {}
    );
  }


  function getShouldFor(
    row
  ) {
    return numberOrNull(
      getScope(row)
        ?.should_goals
        ?.for
        ?.[valueMode]
    );
  }


  function getShouldAgainst(
    row
  ) {
    return numberOrNull(
      getScope(row)
        ?.should_goals
        ?.against
        ?.[valueMode]
    );
  }


  function getShouldQuotient(
    row
  ) {
    const shouldFor =
      getShouldFor(row);

    const shouldAgainst =
      getShouldAgainst(row);

    if (
      shouldFor === null ||
      shouldAgainst === null ||
      shouldAgainst === 0
    ) {
      return null;
    }

    return (
      shouldFor /
      shouldAgainst
    );
  }


  function getShouldSum(
    row
  ) {
    const shouldFor =
      getShouldFor(row);

    const shouldAgainst =
      getShouldAgainst(row);

    if (
      shouldFor === null ||
      shouldAgainst === null
    ) {
      return null;
    }

    return (
      shouldFor +
      shouldAgainst
    );
  }


  function getSortValue(
    row,
    key
  ) {
    const scope =
      getScope(row);

    if (
      key === "team"
    ) {
      return String(
        row?.team_name ||
        ""
      ).toLowerCase();
    }

    if (
      key === "games"
    ) {
      return Number(
        scope?.games ||
        0
      );
    }

    if (
      key === "should_quotient"
    ) {
      return getShouldQuotient(
        row
      );
    }

    if (
      key === "should_sum"
    ) {
      return getShouldSum(
        row
      );
    }

    if (
      key === "should_for"
    ) {
      return getShouldFor(
        row
      );
    }

    if (
      key === "should_against"
    ) {
      return getShouldAgainst(
        row
      );
    }

    const [
      typeId,
      side,
    ] =
      String(key).split(":");

    return numberOrNull(
      scope
        ?.statistics
        ?.[typeId]
        ?.[side]
        ?.[valueMode]
    );
  }


  function handleSort(
    key
  ) {
    if (
      sortKey === key
    ) {
      setSortDirection(
        (current) =>
          current === "asc"
            ? "desc"
            : "asc"
      );

      return;
    }

    setSortKey(
      key
    );

    setSortDirection(
      key === "team"
        ? "asc"
        : "desc"
    );
  }


  function compareValues(
    a,
    b
  ) {
    const aValue =
      getSortValue(
        a,
        sortKey
      );

    const bValue =
      getSortValue(
        b,
        sortKey
      );

    if (
      aValue === null ||
      aValue === undefined
    ) {
      return 1;
    }

    if (
      bValue === null ||
      bValue === undefined
    ) {
      return -1;
    }

    let comparison = 0;

    if (
      typeof aValue ===
        "string" ||
      typeof bValue ===
        "string"
    ) {
      comparison =
        String(
          aValue
        ).localeCompare(
          String(
            bValue
          ),
          "de"
        );
    } else {
      comparison =
        Number(aValue) -
        Number(bValue);
    }

    return (
      sortDirection ===
      "asc"
        ? comparison
        : -comparison
    );
  }


  const sortedRows =
    [...rows].sort(
      compareValues
    );


  function weightedAverage(
    getter
  ) {
    let weightedSum = 0;
    let count = 0;

    for (
      const row of rows
    ) {
      const result =
        getter(row);

      const average =
        numberOrNull(
          result?.average
        );

      const itemCount =
        numberOrNull(
          result?.count
        );

      if (
        average === null ||
        itemCount === null ||
        itemCount <= 0
      ) {
        continue;
      }

      weightedSum +=
        average *
        itemCount;

      count +=
        itemCount;
    }

    return count > 0
      ? weightedSum /
          count
      : null;
  }


  const leagueShouldFor =
    weightedAverage(
      (row) =>
        getScope(row)
          ?.should_goals
          ?.for
    );

  const leagueShouldAgainst =
    weightedAverage(
      (row) =>
        getScope(row)
          ?.should_goals
          ?.against
    );

  const leagueShouldQuotient =
    (
      leagueShouldFor !== null &&
      leagueShouldAgainst !== null &&
      leagueShouldAgainst !== 0
    )
      ? (
          leagueShouldFor /
          leagueShouldAgainst
        )
      : null;

  const leagueShouldSum =
    (
      leagueShouldFor !== null &&
      leagueShouldAgainst !== null
    )
      ? (
          leagueShouldFor +
          leagueShouldAgainst
        )
      : null;


  function SortHeader({
    label,
    sort,
    align = "center",
    prominent = false,
    minWidth = 64,
  }) {
    return (
      <TableCell
        align={align}
        sx={{
          fontWeight:
            prominent
              ? 900
              : 800,

          minWidth,

          /*
           * Sticky Header braucht eine vollständig
           * deckende Hintergrundfarbe.
           *
           * action.selected ist absichtlich transparent
           * und ließ deshalb beim Scrollen die Werte
           * der Tabellenzeilen durch Quotient/Summe
           * hindurchscheinen.
           */
          px: 0.55,
          py: 0.45,
          fontSize: "0.72rem",
          lineHeight: 1.1,
          whiteSpace: "nowrap",

          bgcolor: (theme) =>
            prominent
              ? (
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200]
                )
              : theme.palette.background.paper,
        }}
      >
        <TableSortLabel
          active={
            sortKey ===
            sort
          }
          direction={
            sortKey === sort
              ? sortDirection
              : "asc"
          }
          onClick={() =>
            handleSort(
              sort
            )
          }
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  }


  if (
    rows.length === 0
  ) {
    return (
      <Alert severity="info">
        Keine Team-Statistiken vorhanden.
      </Alert>
    );
  }


  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        overflowX: "auto",
        overflowY: "visible",
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth:
            650 +
            statisticTypes.length *
              118,

          "& .MuiTableCell-root": {
            px: 0.55,
            py: 0.35,
            fontSize:
              "0.76rem",
            lineHeight: 1.12,
            whiteSpace:
              "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              align="right"
              sx={{
                fontWeight: 800,
                width: 34,
                minWidth: 34,
                position: "sticky",
                left: 0,
                zIndex: 5,
                bgcolor:
                  "background.paper",
              }}
            >
              #
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 800,
                width: 170,
                minWidth: 170,
                maxWidth: 170,
                position: "sticky",
                left: 34,
                zIndex: 4,
                bgcolor:
                  "background.paper",
              }}
            >
              <TableSortLabel
                active={
                  sortKey ===
                  "team"
                }
                direction={
                  sortKey === "team"
                    ? sortDirection
                    : "asc"
                }
                onClick={() =>
                  handleSort(
                    "team"
                  )
                }
              >
                Team
              </TableSortLabel>
            </TableCell>

            <SortHeader
              label="Sp"
              sort="games"
              minWidth={42}
            />

            <SortHeader
              label="Quotient"
              sort="should_quotient"
              prominent
              minWidth={70}
            />

            <SortHeader
              label="Summe"
              sort="should_sum"
              prominent
              minWidth={62}
            />

            <SortHeader
              label="Should F"
              sort="should_for"
              minWidth={64}
            />

            <SortHeader
              label="Should A"
              sort="should_against"
              minWidth={64}
            />

            {statisticTypes.map(
              ([typeId, title]) => (
                <React.Fragment
                  key={typeId}
                >
                  <SortHeader
                    label={`${title} F`}
                    sort={`${typeId}:for`}
                  />

                  <SortHeader
                    label={`${title} A`}
                    sort={`${typeId}:against`}
                  />
                </React.Fragment>
              )
            )}
          </TableRow>
        </TableHead>


        <TableBody>
          {sortedRows.map(
            (row, index) => {
              const scope =
                getScope(row);

              const shouldFor =
                getShouldFor(row);

              const shouldAgainst =
                getShouldAgainst(
                  row
                );

              const quotient =
                getShouldQuotient(
                  row
                );

              const sum =
                getShouldSum(
                  row
                );

              return (
                <TableRow
                  key={
                    row.team_id
                  }
                  hover
                >
                  <TableCell
                    align="right"
                    sx={{
                      width: 34,
                      minWidth: 34,
                      position:
                        "sticky",
                      left: 0,
                      zIndex: 3,
                      bgcolor:
                        "background.paper",
                      fontWeight: 800,
                    }}
                  >
                    {index + 1}
                  </TableCell>

                  <TableCell
                    title={
                      row.team_name ||
                      `Team ${row.team_id}`
                    }
                    sx={{
                      width: 170,
                      minWidth: 170,
                      maxWidth: 170,
                      overflow:
                        "hidden",
                      textOverflow:
                        "ellipsis",
                      fontWeight: 700,
                      position:
                        "sticky",
                      left: 34,
                      zIndex: 2,
                      bgcolor:
                        "background.paper",
                    }}
                  >
                    {row.team_name ||
                      `Team ${row.team_id}`}
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    {scope.games ??
                      0}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 900,
                      bgcolor:
                        "action.hover",
                    }}
                  >
                    {formatStatisticValue(
                      quotient,
                      3
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 900,
                      bgcolor:
                        "action.hover",
                    }}
                  >
                    {formatStatisticValue(
                      sum
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                    title={
                      `Basis: ${
                        scope
                          ?.should_goals
                          ?.for
                          ?.count ??
                        0
                      } Fixtures`
                    }
                  >
                    {formatStatisticValue(
                      shouldFor
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                    title={
                      `Basis: ${
                        scope
                          ?.should_goals
                          ?.against
                          ?.count ??
                        0
                      } Fixtures`
                    }
                  >
                    {formatStatisticValue(
                      shouldAgainst
                    )}
                  </TableCell>

                  {statisticTypes.map(
                    ([typeId]) => {
                      const statistic =
                        scope
                          ?.statistics
                          ?.[typeId] ||
                        {};

                      return (
                        <React.Fragment
                          key={typeId}
                        >
                          <TableCell
                            align="center"
                            title={
                              `Basis: ${
                                statistic
                                  ?.for
                                  ?.count ??
                                0
                              } Fixtures`
                            }
                          >
                            {formatStatisticValue(
                              statistic
                                ?.for
                                ?.[valueMode]
                            )}
                          </TableCell>

                          <TableCell
                            align="center"
                            title={
                              `Basis: ${
                                statistic
                                  ?.against
                                  ?.count ??
                                0
                              } Fixtures`
                            }
                          >
                            {formatStatisticValue(
                              statistic
                                ?.against
                                ?.[valueMode]
                            )}
                          </TableCell>
                        </React.Fragment>
                      );
                    }
                  )}
                </TableRow>
              );
            }
          )}


          <TableRow
            sx={{
              "& td": {
                fontWeight: 800,
                borderTop:
                  "2px solid",
                borderColor:
                  "divider",
                bgcolor:
                  "action.hover",
              },
            }}
          >
            <TableCell
              align="right"
              sx={{
                width: 34,
                minWidth: 34,
                position:
                  "sticky",
                left: 0,
                zIndex: 3,
              }}
            >
              —
            </TableCell>

            <TableCell
              sx={{
                position:
                  "sticky",
                left: 34,
                zIndex: 2,
              }}
            >
              Liga Ø
            </TableCell>

            <TableCell
              align="center"
            >
              —
            </TableCell>

            <TableCell
              align="center"
            >
              {formatStatisticValue(
                leagueShouldQuotient,
                3
              )}
            </TableCell>

            <TableCell
              align="center"
            >
              {formatStatisticValue(
                leagueShouldSum
              )}
            </TableCell>

            <TableCell
              align="center"
            >
              {formatStatisticValue(
                leagueShouldFor
              )}
            </TableCell>

            <TableCell
              align="center"
            >
              {formatStatisticValue(
                leagueShouldAgainst
              )}
            </TableCell>

            {statisticTypes.map(
              ([typeId]) => (
                <React.Fragment
                  key={typeId}
                >
                  <TableCell
                    align="center"
                  >
                    {formatStatisticValue(
                      weightedAverage(
                        (row) =>
                          getScope(
                            row
                          )
                            ?.statistics
                            ?.[typeId]
                            ?.for
                      )
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    {formatStatisticValue(
                      weightedAverage(
                        (row) =>
                          getScope(
                            row
                          )
                            ?.statistics
                            ?.[typeId]
                            ?.against
                      )
                    )}
                  </TableCell>
                </React.Fragment>
              )
            )}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
